import { useQuery } from '@apollo/client'
import { Navigate } from '@faceup/router'
import type { ResultOf } from '@graphql-typed-document-node/core'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect } from 'react'
import { graphql } from '../__generated__'
import { LanguageContext } from './LanguageContext'
import { ReportSourceContext } from './ReportSourceProvider'
import { UserContext } from './UserContext'

const query = graphql(`
  query SurveySourceProviderQuery($reportSourceId: PublicReportSourceGlobalId!) {
    publicSurvey(reportSourceId: $reportSourceId) {
      id
      defaultLanguage
      logoUrl
      sourceType
      ...SurveyHeader_survey
      institution {
        id
        billing {
          id
          plan
        }
        isTesting
        type
        organizationalUnitName
        config {
          id
          institutionName
          primaryColor
          isBrandingHidden
          ...AppWrapper_config
        }
        partner {
          id
          name
          config {
            id
            primaryColor
            isBrandingHidden
            rectangularLogoUrl
            ...AppWrapper_config
          }
        }
      }
    }
  }
`)

export type SurveyData = NonNullable<ResultOf<typeof query>['publicSurvey']>
export const SurveyDataContext = createContext<SurveyData | null>(null)

type SurveySourceProviderProps = {
  children: ReactNode
}

const SurveySourceProvider = ({ children }: SurveySourceProviderProps) => {
  const { reportSource } = useContext(UserContext)
  const { language, changeLanguage } = useContext(LanguageContext)

  const { data, loading } = useQuery(query, {
    variables: {
      reportSourceId: reportSource?.defaultFormId ?? '',
    },
    skip: !reportSource?.defaultFormId,
  })

  const survey = data?.publicSurvey

  useEffect(() => {
    if (survey?.defaultLanguage && survey.defaultLanguage !== language) {
      changeLanguage(survey.defaultLanguage)
    }
  }, [survey, changeLanguage, language])

  if (!loading && reportSource?.defaultFormId && !survey) {
    return <Navigate to={routes => routes.home()} replace />
  }

  return (
    <ReportSourceContext.Provider
      value={
        survey
          ? {
              id: survey.id,
              plan: survey.institution.billing?.plan ?? null,
              isTesting: survey.institution.isTesting ?? null,
              type: survey.institution.type ?? null,
              name: survey.institution.organizationalUnitName ?? null,
              institutionName: survey.institution.config?.institutionName ?? '',
              homepage: routes => routes.survey(),
            }
          : null
      }
    >
      <SurveyDataContext.Provider value={survey ?? null}>{children}</SurveyDataContext.Provider>
    </ReportSourceContext.Provider>
  )
}

export default SurveySourceProvider
